import React, { useEffect } from "react";
import { Popper, Paper, Typography, Box } from "@mui/material";
import PropTypes from "prop-types";
import makeStyles from "@mui/styles/makeStyles";
import ReactAutosuggest from "react-autosuggest";

const useStyles = makeStyles(theme => ({
  suggestionsList: {
    padding: 0,
    margin: 0,
    listStyleType: "none",
    maxHeight: 390,
    overflowY: "scroll"
  },
  suggestion: {
    minHeight: 36,
    padding: theme.spacing(1.25),
    cursor: "pointer"
  },
  listContainer: {
    padding: 0
  },
  popper: {
    zIndex: 1305
  },
  suggestionHighlighted: {
    backgroundColor: theme.palette.action.hover
  }
}));

/**
 * Splits suggestions into array of strings to check for a match to the search value
 */
function splitSuggestion(suggestion) {
  const suggestionWords = suggestion.split(" ");
  const possibleMatches = suggestionWords.map((suggestion, index) => {
    let possibleMatch = "";
    for (let i = index; i < suggestionWords.length; i++) {
      possibleMatch += `${suggestionWords[i]} `;
    }
    return possibleMatch.trim();
  });
  return possibleMatches;
}

const getSuggestionValues = (suggestionsList, inputValue) => {
  let list = suggestionsList.filter(suggestion => {
    const keep = splitSuggestion(suggestion.text).find(
      part => part.slice(0, inputValue.length).toLowerCase() === inputValue
    );
    return keep;
  });
  return list;
};

function getSuggestions(suggestions, value, multiSection) {
  const valueText = value && value.text ? value.text : value;
  const inputValue = valueText.trim().toLowerCase();
  const inputLength = inputValue.length;
  const suggestionsList =
    inputLength === 0
      ? suggestions // sets suggestions list to original list of all suggestions if there's no input
      : multiSection
      ? suggestions.map(suggestion => ({
          sectionName: suggestion.sectionName,
          titles: getSuggestionValues(suggestion.titles, inputValue)
        }))
      : getSuggestionValues(suggestions, inputValue);
  return multiSection
    ? suggestionsList.filter(section => section.titles.length > 0)
    : suggestionsList;
}

function getSuggestionValue(suggestion) {
  return suggestion;
}

function getSectionSuggestions(section) {
  return section.titles;
}

export default function Autosuggest({
  renderInputComponent,
  anchorEl,
  multiSection,
  renderSuggestionText,
  suggestions,
  onSuggestionSelected,
  inputProps,
  onTextInputChange,
  defaultValue
}) {
  const classes = useStyles();
  const [stateSuggestions, setSuggestions] = React.useState(suggestions);
  const [showSuggestionsList, setShowSuggestionsList] = React.useState(false);
  const [value, setValue] = React.useState(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
    onTextInputChange(defaultValue);
  }, [defaultValue]);

  function renderSuggestion(suggestion, { query }) {
    return renderSuggestionText(suggestion);
  }

  function renderSectionTitle(section) {
    return (
      <Typography variant="subtitle2" color="textSecondary">
        {section.sectionName}
      </Typography>
    );
  }

  const handleSuggestionsFetchRequested = ({ value }) => {
    setSuggestions(getSuggestions(suggestions, value, multiSection));
  };

  const handleSuggestionsClearRequested = () => {
    setSuggestions(suggestions);
  };

  const handleChange = () => (event, { newValue }) => {
    setValue(newValue);
    onTextInputChange(newValue);
  };

  const handleSuggestedSelected = (event, { suggestion }) => {
    setValue(suggestion.text);
    if (onSuggestionSelected) {
      onSuggestionSelected(suggestion);
    }
  };

  const autosuggestProps = {
    renderInputComponent,
    suggestions: stateSuggestions,
    onSuggestionsFetchRequested: handleSuggestionsFetchRequested,
    onSuggestionsClearRequested: handleSuggestionsClearRequested,
    getSuggestionValue,
    renderSuggestion,
    renderSectionTitle: multiSection ? renderSectionTitle : null,
    getSectionSuggestions: multiSection ? getSectionSuggestions : null,
    onSuggestionSelected: handleSuggestedSelected
  };
  return (
    <Box flex={1}>
      <ReactAutosuggest
        {...autosuggestProps}
        alwaysRenderSuggestions={showSuggestionsList}
        multiSection={multiSection}
        focusInputOnSuggestionClick={false}
        inputProps={{
          id: "react-autosuggest-popper",
          value: value.text ? value.text : value,
          onChange: handleChange(),
          onFocus: () => setShowSuggestionsList(true),
          onBlur: () => setShowSuggestionsList(false),
          ...inputProps
        }}
        theme={{
          suggestionsList: classes.suggestionsList,
          suggestion: classes.suggestion,
          sectionContainer: classes.suggestionsSection,
          suggestionHighlighted: classes.suggestionHighlighted
        }}
        renderSuggestionsContainer={options => (
          <Popper
            anchorEl={anchorEl}
            open={Boolean(options.children)}
            className={classes.popper}
          >
            <Paper
              square
              classes={{ root: classes.listContainer }}
              {...options.containerProps}
              style={{ width: anchorEl ? anchorEl.clientWidth : undefined }}
            >
              {options.children}
            </Paper>
          </Popper>
        )}
      />
    </Box>
  );
}

Autosuggest.propTypes = {
  anchorEl: PropTypes.shape({
    clientWidth: PropTypes.number
  }),
  multiSection: PropTypes.bool,
  renderInputComponent: PropTypes.func,
  renderSuggestionText: PropTypes.func,
  suggestions: PropTypes.array,
  placeholder: PropTypes.string
};

Autosuggest.defaultProps = {
  suggestions: []
};
